import { API_IDS } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { IHttpAdapter } from '../../core/entities/iHttpAdapter';
import { IAuthRepository } from '../../core/repositories/interfaces/iAuthRepository';
import { container } from '../../DataDomain';
import { ILocateUserEntity, IVerifyUserResponse } from '../../entity';
import {
    IApisLoginResponse,
    IApisVerifyUsernameResponse,
    IAuthApisFormSigatureResponse,
    IAuthApisRefreshAuthTokenResponse,
    IDevicePairRequest,
    IDevicePairResponse,
    IForgotPasswordResetResponse,
    IForgotPasswordSendOTPResponse,
    IForgotPasswordVerifyOTPResponse,
    IZendeskJWTTokenPayload,
    IZendeskJWTTokenResponse,
    IApisLogoutResponse,
    IForgetResendPinRequest,
    IForgetResendPinResponse,
    IForgetPinResponse,
    IVerifyEmailRequest, IVerifyEmailResponse,
} from '../../entity/interface/iAuthApis';
import { IApiPathManager } from '../config/ApiPathManager';

class AuthRepository implements IAuthRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    /**
     * Get CSRF form signature
     * @returns {data: {token: string, tokenName: string}}
     */
    async getFormSignature(): Promise<IAuthApisFormSigatureResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_FORM_SIGNATURE);
        const response = await this.http.get<IAuthApisFormSigatureResponse>(path, {
            withCredentials: true,
        });
        return response.data;
    }
    async refreshAuthToken(
        username: string,
        authToken: string,
        identityToken: string,
        authSign: string,
        apiToken: { [key: string]: string }
    ): Promise<IAuthApisRefreshAuthTokenResponse> {
        const path = this.apiPathManager
            .getPath(API_IDS.AUTH_REFRESH_TOKEN)
            .replace('{hostname}', process.env['NX_PUBLIC_MLP_API_BASE_URL'] || '');
        const response = await this.http.post<IAuthApisRefreshAuthTokenResponse>(path, {
            withCredentials: true,
            body: {
                username,
                authToken,
                identityToken,
                authSign,
                ...apiToken,
            },
        });
        return response.data;
    }

    async zendeskJWTToken(
        payload: IZendeskJWTTokenPayload,
        authToken: string,
        authSign: string,
        identityToken: string,
        apiToken: { [key: string]: string }
    ): Promise<IZendeskJWTTokenResponse> {
        const zenDeskPathClassic = this.apiPathManager.getPath(API_IDS.ZENDESK_JWT_TOKEN_CLASSIC);
        const zenDeskPath = this.apiPathManager.getPath(API_IDS.ZENDESK_JWT_TOKEN);
        const apiUrl = payload?.isClassic === true ? zenDeskPathClassic : zenDeskPath;
        const response = await this.http.post<IZendeskJWTTokenResponse>(
            apiUrl.replace('{hostname}', process.env['NX_PUBLIC_MLP_API_BASE_URL'] || ''),
            {
                withCredentials: true,
                body: {
                    ...payload,
                    authToken,
                    authSign,
                    identityToken,
                    ...apiToken,
                },
            }
        );
        return response.data;
    }

    async devicePairing(
        payload: IDevicePairRequest,
        apiToken: { [key: string]: string }
    ): Promise<IDevicePairResponse> {
        const path = this.apiPathManager.getPath(API_IDS.DEVICE_PAIRING);
        const response = await this.http.post<IDevicePairResponse>(path, {
            withCredentials: true,
            body: {
                ...payload,
                ...apiToken,
            },
        });
        return response.data;
    }

    /**
     * Verify username and send OTP if OTP login enabled
     * @param username
     * @param lang
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken Recaptcha token if enabled
     * @returns Auth user object
     */
    async verifyUsername(
        username: string,
        lang: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<IApisVerifyUsernameResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_LOGIN_VERIFY);
        const response = await this.http.post<IApisVerifyUsernameResponse>(path, {
            withCredentials: true,
            body: {
                username,
                lang,
                recaptchaToken,
                ...apiToken,
            },
        });
        return response.data;
    }

    /**
     * Verify username and send OTP if OTP login enabled
     * @param username
     * @param lang
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken Recaptcha token if enabled
     * @returns Auth user object
     */
    async locateUser(
        username: string,
        lang: string,
        apiToken: { [key: string]: string },
    ): Promise<ILocateUserEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_LOCATE_USER);
        const response = await this.http.post<IApisVerifyUsernameResponse>(path, {
            withCredentials: true,
            body: {
                username,
                lang,
                ...apiToken,
            },
        });
        return response.data;
    }

    /**
     * Login with credentials
     * @param username
     * @param password
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken Recaptcha token if enabled
     * @returns Auth user object
     */
    async loginWithCredentials(
        username: string,
        password: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<IApisLoginResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_LOGIN_CREDENTIALS);
        const response = await this.http.post<IApisLoginResponse>(path, {
            withCredentials: true,
            body: {
                username,
                password,
                recaptchaToken,
                ...apiToken,
            },
        });
        return response.data;
    }

    /**
     * Login with OTP
     * @param username
     * @param otp
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken
     * @returns
     */
    async loginWithOTP(
        username: string,
        otp: string,
        lang: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<IApisLoginResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_LOGIN_OTP);
        const response = await this.http.post<IApisLoginResponse>(path, {
            withCredentials: true,
            body: {
                lang,
                username,
                otp,
                recaptchaToken,
                ...apiToken,
            },
        });
        return response.data;
    }

    /**
     * Forgot password send OTP
     * @param username
     * @param lang
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken Recaptcha token if enabled
     * @returns
     */
    async forgotPasswordSendOTP(
        username: string,
        lang: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<IForgotPasswordSendOTPResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_FORGOT_PASSWORD_SEND_OTP);
        return this.http
            .post<IForgotPasswordSendOTPResponse>(path, {
                withCredentials: true,
                body: {
                    username,
                    lang,
                    recaptchaToken,
                    ...apiToken,
                },
            })
            .then((res) => res.data);
    }

    /**
     * Forgot password Resend OTP
     * @param username
     * @param lang
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken Recaptcha token if enabled
     * @returns
     */
    async forgotPasswordResendOTP(
        username: string,
        lang: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<IForgotPasswordSendOTPResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_FORGOT_PASSWORD_RESEND_OTP);
        return this.http
            .post<IForgotPasswordSendOTPResponse>(path, {
                withCredentials: true,
                body: {
                    username,
                    lang,
                    recaptchaToken,
                    ...apiToken,
                },
            })
            .then((res) => res.data);
    }

    /**
     * Forgot password Verify OTP
     * @param username
     * @param pinValue
     * @param verifyGuid
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken Recaptcha token if enabled
     * @returns
     */
    async forgotPasswordVerifyOTP(
        username: string,
        pinValue: string,
        verifyGuid: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<IForgotPasswordVerifyOTPResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_FORGOT_PASSWORD_VERIFY_OTP);
        return this.http
            .post<IForgotPasswordVerifyOTPResponse>(path, {
                withCredentials: true,
                body: {
                    username,
                    pin_value: pinValue,
                    verify_guid: verifyGuid,
                    recaptchaToken,
                    ...apiToken,
                },
            })
            .then((res) => res.data);
    }

    /**
     * Forgot password Reset
     * @param username
     * @param pinValue
     * @param verifyGuid
     * @param password
     * @param apiToken Form signature CSRF and key object
     * @param recaptchaToken Recaptcha token if enabled
     * @returns
     */
    async forgotPasswordReset(
        username: string,
        pinValue: string,
        verifyGuid: string,
        password: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<IForgotPasswordResetResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_FORGOT_PASSWORD_RESET);
        return this.http
            .post<IForgotPasswordResetResponse>(path, {
                withCredentials: true,
                body: {
                    username,
                    pin_value: pinValue,
                    verify_guid: verifyGuid,
                    password,
                    recaptchaToken,
                    ...apiToken,
                },
            })
            .then((res) => res.data);
    }

    /**
     * Verify QR code and get user auth object in response.
     * @param qrCode
     * @param make
     * @param model
     * @param platform
     * @param lang
     * @param apiToken Form signature CSRF and key object
     * @returns Auth user object
     */
    async verifyQRCode(
        qrCode: string,
        make: string,
        model: string,
        platform: string,
        lang: string,
        apiToken: { [key: string]: string }
    ): Promise<IApisLoginResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_API_VERIFY_QR_CODE);
        const response = await this.http.post<IApisLoginResponse>(path, {
            withCredentials: true,
            body: {
                qr_code: qrCode,
                make,
                model,
                platform,
                lang,
                ...apiToken,
            },
        });
        return response.data;
    }

    /**
     *
     * Verify username is available or not
     * @param username
     * @param apiToken
     * @param recaptchaToken
     * @returns user verification details
     */
    async verifyUser(
        username: string,
        recaptchaToken: string | null,
        apiToken: { [key: string]: string }
    ): Promise<IVerifyUserResponse> {
        const path = this.apiPathManager.getPath(API_IDS.USER_VERIFY_USER);
        const response = await this.http.post<IVerifyUserResponse>(path, {
            withCredentials: true,
            body: {
                username,
                recaptchaToken,
                ...apiToken,
            },
        });
        return response.data;
    }

    async autoLogin(
        username: string,
        identityToken: string,
        authSign: string,
        userLoginToken: string,
        apiToken: { [key: string]: string },
        recaptchaToken: string | null
    ): Promise<any> {
        const path = this.apiPathManager.getPath(API_IDS.AUTO_LOGIN);
        const response = await this.http.post<any>(path, {
            withCredentials: true,
            body: {
                username,
                identityToken,
                authSign,
                userLoginToken,
                recaptchaToken,
                ...apiToken,
            },
        });
        return response.data;
    }

    async logout(
        username: string,
        authToken: string,
        identityToken: string,
        authSign: string,
        apiToken: { [key: string]: string }
    ): Promise<IApisLogoutResponse> {
        const path = this.apiPathManager.getPath(API_IDS.LOGOUT_API);
        const response = await this.http.post<any>(path, {
            withCredentials: true,
            body: {
                username,
                authToken,
                identityToken,
                authSign,
                ...apiToken,
            },
        });
        return response.data;
    }

    async getGuestPublicConfig(url: string): Promise<unknown | null> {
        const response = await this.http.get<unknown>(url, {
            withCredentials: false,
        });
        return response.data;
    }

    async loginGuest(
        url: string,
        payload: { username: string; envKey: string; client: string }
    ): Promise<unknown | null> {
        const response = await this.http.post<unknown>(url, {
            withCredentials: false,
            body: payload,
        });
        return response.data;
    }

    async forgetResendPin(
        payload: IForgetResendPinRequest,
        apiToken: { [key: string]: string }
    ): Promise<IForgetResendPinResponse> {
        const path = this.apiPathManager.getPath(API_IDS.FORGET_PIN_RESEND);
        return this.http
            .post<IForgetResendPinResponse>(path, {
                withCredentials: true,
                body: {
                    ...payload,
                    ...apiToken,
                },
            })
            .then((res) => res.data);
    }

    async b2bForgotPin(apiKey: string, username: string, identityToken: string, authToken: string, apiToken: { [key: string]: string }): Promise<IForgetPinResponse> {
        const response = await this.http.post<IForgetPinResponse>(this.apiPathManager.getPath(API_IDS.POPCORN_API_FORGOT_PIN), {
            withCredentials: true,
            body: {
                identityToken,
                authToken: authToken,
                apiKey: apiKey,
                username: username,
                ...apiToken,
            },
        });
        return response.data;
    }

    /**
     *
     * Verify email
     * @param username
     * @returns email verification details
     */
    async verifyEmail(
        payload: IVerifyEmailRequest,
    ): Promise<IVerifyEmailResponse> {
        const path = this.apiPathManager.getPath(API_IDS.VERIFY_EMAIL);
        console.log('tokenn payload', path)
        const response = await this.http.put<IVerifyEmailResponse>(path, {
            withCredentials: true,
            body: {
                ...payload,
            },
        });
        return response.data;
    }

}

export default AuthRepository;
